"use client";

import {
  Checkbox,
  ListItem,
  ListItemContent,
  ListItemIcon,
  ListItemText,
  RadioGroupIndicator,
  RadioGroupItem,
} from "@dafiti/closet-ui";
import { cn } from "@dafiti/closet-ui/utils";
import type { ReactNode } from "react";

type FilterOptionListItemProps = {
  value: string;
  label: string;
  checked: boolean;
  selector: "Multiple" | "Single";
  prefix?: ReactNode;
  hideDivider?: boolean;
  isCompact?: boolean;
  className?: string;
  onToggle: (value: string, checked: boolean) => void;
};

export function FilterOptionListItem({
  value,
  label,
  checked,
  selector,
  prefix,
  hideDivider = false,
  isCompact = false,
  className,
  onToggle,
}: FilterOptionListItemProps) {
  const handleClick = () => {
    if (selector === "Multiple") {
      onToggle(value, !checked);
    }
  };

  const dividerClass = hideDivider ? "" : "lg:border-b lg:border-gray-200";

  if (selector === "Single") {
    return (
      <ListItem
        as="label"
        className={cn(
          cn("w-full cursor-pointer p-0", isCompact ? "py-2" : "py-4"),
          dividerClass,
          className,
        )}
        data-testid={`filter-option-item-${value}`}
      >
        <ListItemIcon className={cn("w-max", isCompact && "h-7 lg:h-[30px]")}>
          <RadioGroupItem
            value={value}
            aria-label={label}
            data-testid={`filter-option-radio-${value}`}
          >
            <RadioGroupIndicator />
          </RadioGroupItem>

          {prefix && prefix}
        </ListItemIcon>

        <ListItemContent className="flex-row items-center">
          <ListItemText className="text-body-3! lg:text-body-2! font-normal">
            {label}
          </ListItemText>
        </ListItemContent>
      </ListItem>
    );
  }

  return (
    <ListItem
      as="div"
      className={cn(
        "w-full cursor-pointer p-0",
        isCompact ? "py-2" : "py-4",
        dividerClass,
        className,
      )}
      onClick={handleClick}
      data-testid={`filter-option-item-${value}`}
    >
      <ListItemIcon className={cn("w-max", isCompact && "h-7 lg:h-[30px]")}>
        <Checkbox
          className="w-5 h-5"
          checked={checked}
          aria-label={label}
          data-testid={`filter-option-checkbox-${value}`}
        />

        {prefix && prefix}
      </ListItemIcon>

      <ListItemContent className="flex-row items-center">
        <ListItemText className="text-body-3! lg:text-body-2! font-normal">
          {label}
        </ListItemText>
      </ListItemContent>
    </ListItem>
  );
}

export type { FilterOptionListItemProps };
